import type { AuthDto } from '@mathflat/domain/@entities/Auth'
import { jwtTokenService } from '@mathflat/domain/@entities/Auth/jwtTokenService'
import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain.ts'
import { AxiosError } from 'axios'

import { loadInitialData } from '~/@app/init/loadInitialData'

import { authApi } from '../api/authApi'
import { LATEST_DEVICE_VERSION, publicRoutes, routeName } from '../constants'
import { crossOriginLogoutClear } from '../integration/crossOriginLogoutClear'
import { probeReceiverDeployed } from '../integration/handleStudentUpLogic'
import { getDomainPair, isSenderSide } from '../integration/integrationDomain'
import { envUtils } from '../utils/envUtils'
import { webviewService } from '.'
import { commonRepo } from './repo.service'
import { localStorageService } from './storage.service'

export class AuthService {
  get isKeepSignInChecked() {
    return jwtTokenService.isUsingLocalStorage
  }

  /**
   * jwtTokenService 의 환경/플랫폼/이벤트 핸들러 셋업만 수행
   * 통합 마이그레이션 흐름 (handleStudentMainLogic 등) 이 jwtTokenService 의 baseURL 등을
   * 필요로 하기 때문에, initialize.ts 에서 통합 분기 전에 먼저 호출.
   */
  prepareJwtAuth() {
    const environment = envUtils.isLive
      ? 'live'
      : envUtils.isStaging
        ? 'stg'
        : localStorage.getItem('DEV_API') ?? 'dev'

    const isNamedEnv = environment === 'live' || environment === 'stg' || environment === 'dev'
    jwtTokenService.init({
      env: isNamedEnv ? environment : 'custom',
      customURL: isNamedEnv ? undefined : environment,
      platform: 'STUDENT',
    })

    const redirectToLoginPath = () => {
      if (publicRoutes.has(window.location.pathname)) return
      window.location.replace(routeName.login + window.location.search)
    }

    jwtTokenService.setEventHandlers({
      onTokenCleared: redirectToLoginPath,
      onTokenNotFound: redirectToLoginPath,
      onTokenReceived: () => {
        // ★ hash 마이그레이션 진행 중에는 reload 하지 않음 — 무한 reload 방지
        //   migrateFromHashParam 이 setTokens('LOGIN') broadcast 일으키면 same-origin frame 들이 onTokenReceived 발화 → reload → URL ?hash 그대로 → 다시 init → 다시 마이그 → 다시 broadcast → 무한
        //   hash cleanup 이 끝난 후의 reload (다른 탭 로그인 등) 만 정상 작동
        if (new URLSearchParams(window.location.search).has('hash')) {
          return
        }
        window.location.reload()
      },
      onReissueSuccess: ({ restrictions }) => {
        commonRepo.permission.updateRestrictions(restrictions ?? [])
      },
    })
  }

  async initializeJwtAuth(options?: { skipInitializeAuth?: boolean }) {
    if (options?.skipInitializeAuth) return

    await jwtTokenService.initializeAuth()
  }

  get userType() {
    if (localStorageService.user.parentId) {
      return 'PARENT'
    }
    return 'STUDENT'
  }

  get isLoggedIn() {
    return jwtTokenService.isLoggedIn
  }

  async loginFromBasicStudentApp({
    accessToken,
    refreshToken,
    isKeepSignInChecked,
  }: {
    accessToken: string
    refreshToken: string
    isKeepSignInChecked: boolean
  }) {
    if (isKeepSignInChecked) {
      jwtTokenService.useLocalStorage()
    }

    jwtTokenService.setTokens({ accessToken, refreshToken })
    await jwtTokenService.processJwtLoginResponse({ accessToken, refreshToken })
  }

  async loginByJwt(
    loginRequest: AuthDto.LoginRequestDto,
    keepLoggedIn: boolean,
    beforeLoginId: string,
  ) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    const response = await authApi.login(loginRequest)

    // response가 undefined이거나 accessToken이 없는 경우 Error 객체로 throw
    // (raw response를 throw하면 undefined인 경우 catch에서 instanceof 체크가 모두 실패하여 silent하게 무시됨)
    // maxios interceptor가 404/501+ 응답을 reject 대신 그대로 resolve 시키므로,
    // 그 경우 response 자체가 AxiosError 객체로 들어온다. 원본 AxiosError 를 보존해야
    // 호출 측 catch 에서 instanceof AxiosError + response.data.code 분기로 처리 가능.
    if (!response?.accessToken) {
      if (response instanceof AxiosError) throw response
      throw new Error('로그인 응답이 유효하지 않습니다.')
    }

    await jwtTokenService.processJwtLoginResponse(response)

    localStorageService.update({
      user: {
        beforeLoginId,
        ...(response.userType === 'STUDENT'
          ? { studentId: response.userId as StudentDomain.Id }
          : { parentId: response.userId }),
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    if (response.userType === 'STUDENT') {
      await loadInitialData(response.userId as StudentDomain.Id, response.restrictions)
    }

    return response
  }

  /**
   * 네이버 웍스(웨일스페이스) SSO 로그인.
   * - OAuth 2.0 콜백에서 받은 code로 서버에 JWT 로그인 요청을 보낸다.
   * - SSO 로그인 특성상 "로그인 유지"를 기본으로 한다(keepLoggedIn=true 사용).
   */
  async loginByNaverWorks(code: string, keepLoggedIn: boolean) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    const response = await authApi.postNaverWorksLogin({
      code,
      userType: 'STUDENT',
      serviceType: 'MATHFLAT',
    })

    if (!response?.accessToken) {
      throw response
    }

    await jwtTokenService.processJwtLoginResponse(response)

    localStorageService.update({
      user: {
        ...(response.userType === 'STUDENT'
          ? { studentId: response.userId as StudentDomain.Id }
          : { parentId: response.userId }),
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    if (response.userType === 'STUDENT') {
      await loadInitialData(response.userId as StudentDomain.Id, response.restrictions)
    }

    return response
  }

  /**
   * AIEP(광주교육청 통합 로그인) SAML 브릿지 로그인.
   * - /v2/login/saml2/aiep 는 SAML 콜백 code 를 받아 JWT 세트를 바로 발급한다.
   * - AIEP 는 학생 전용 SSO 이므로 응답이 STUDENT 가 아니면 에러로 던진다.
   */
  async loginByAiep(code: string, keepLoggedIn: boolean) {
    if (keepLoggedIn) {
      jwtTokenService.useLocalStorage()
    }

    const response = await authApi.postAiepLogin({ code, userType: 'STUDENT' })

    // maxios interceptor 가 404/501+ 응답을 reject 대신 그대로 resolve 시키므로
    // 그 경우 response 가 AxiosError 객체로 들어온다. 원본 AxiosError 를 보존해야
    // 호출 측 catch 에서 instanceof AxiosError + response.data.code 분기로 처리 가능.
    if (!response?.accessToken) {
      if (response instanceof AxiosError) throw response
      throw new Error('AIEP 로그인 응답이 유효하지 않습니다.')
    }

    if (response.userType !== 'STUDENT') {
      throw new Error('STUDENT_ONLY')
    }

    await jwtTokenService.processJwtLoginResponse(response)

    const studentId = response.userId as StudentDomain.Id
    localStorageService.update({
      user: {
        studentId,
        academyId: response.academyId,
        isPasswordChangedRequired: response.passwordChangedRequired,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    await loadInitialData(studentId, response.restrictions)

    return response
  }

  /**
   * 선생님앱 iframe에서 학생 대리 로그인.
   */
  async loginByStudentImpersonationAuthToken(token: string) {
    const response = await authApi.loginByStudentImpersonationAuthToken(token)
    await jwtTokenService.processJwtLoginResponse(response)

    // 학부모 → 학생 대리 로그인이므로 이전 학부모 세션을 정리한다.
    // update()는 lodash merge 라 parentId 를 안 비우면 userType 이 PARENT 로 남는다.
    localStorageService.clearParentSession()

    localStorageService.update({
      user: {
        studentId: response.userId as StudentDomain.Id,
        academyId: response.academyId,
      },
      device: { ver: LATEST_DEVICE_VERSION },
    })

    return response
  }

  async logout() {
    const legacyToken = jwtTokenService.getLegacyToken()
    const refreshToken = jwtTokenService.getRefreshToken()

    const logoutPromises: Promise<unknown>[] = []
    if (legacyToken) {
      logoutPromises.push(authApi.legacyLogout({ legacyToken }))
    }
    if (refreshToken) {
      const deviceId = localStorageService.deviceId
      logoutPromises.push(authApi.logout({ refreshToken, deviceId }))
    }

    // API 요청이 완료될 때까지 대기 (실패해도 계속 진행)
    await Promise.allSettled(logoutPromises)

    // ★ sender (student-up) 에서만 probe 결과 의미 있음 — receiver 는 자기 /login 으로 가니 무관.
    //   Phase 1 (반대편 student 가 아직 베이직) 케이스에서 probe 실패 시 베이직 #/logout 으로 fallback.
    const isOppositeIntegrated = isSenderSide() ? await probeReceiverDeployed() : true

    // 반대 origin (student ↔ student-up) 의 storage 도 정리. 반대편이 통합인 경우만 의미 있음.
    if (isOppositeIntegrated) {
      await crossOriginLogoutClear()
    }

    sessionStorage.clear()
    localStorageService.clear()
    jwtTokenService.clearTokens()
    webviewService.clearAppBadges()
    webviewService.logout()

    // redirect 분기:
    //   ① sender + opposite 통합  → opposite /login (Phase 2 정상 흐름)
    //   ② sender + opposite 베이직 → 베이직 학생앱 #/logout (기존 staging 흐름 보존)
    //   ③ receiver                → 자기 /login
    const pair = getDomainPair()
    if (isSenderSide() && pair && isOppositeIntegrated) {
      window.location.replace(`${pair.iframeOrigin}${routeName.login}`)
    } else if (isSenderSide() && !isOppositeIntegrated) {
      envUtils.redirectToBasicStudentApp()
    } else {
      window.location.replace(routeName.login)
    }
  }

  requestToken() {
    jwtTokenService.requestToken()
  }

  requestLogout() {
    jwtTokenService.clearTokens()
  }
}

const authService = new AuthService()
export default authService
